import React from "react";

// Curseur glissant pour l'IDR : rail avec 5 crans (0/25/50/75/100 %).
// Le patient glisse le curseur ou clique un cran. Chaque cran conserve
// le data-testid `option-<id>-<value>` pour compatibilité de test.
export const IdrSlider = ({ id, statement, index, scale, value, onChange, anchorLeft, anchorRight, hint }) => {
  const trackRef = React.useRef(null);
  const draggingRef = React.useRef(false);
  const stops = scale.map((s) => s.value);
  const answered = Number.isFinite(value);
  const current = answered ? scale.find((s) => s.value === value) : null;

  const nearestStop = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const raw = ratio * 100;
    return stops.reduce((a, b) => (Math.abs(b - raw) < Math.abs(a - raw) ? b : a), stops[0]);
  };

  const setFromEvent = (e) => {
    if (!trackRef.current) return;
    const v = nearestStop(e.clientX);
    if (v !== value) onChange(v);
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromEvent(e);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromEvent(e);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e) => {
    const idx = answered ? stops.indexOf(value) : -1;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      onChange(stops[Math.min(stops.length - 1, idx + 1)]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      onChange(stops[Math.max(0, (idx < 0 ? 1 : idx) - 1)]);
    }
  };

  return (
    <div
      className="rounded-2xl border border-[var(--border)] bg-white/70 p-4 sm:p-6 transition-shadow hover:shadow-sm print-break"
      data-testid={`question-${id}`}
    >
      <p className="text-[var(--ink)] text-base leading-snug mb-2">
        {index != null && <span className="text-[var(--gold)] font-semibold mr-2">{index}.</span>}
        {statement}
      </p>

      <div className="text-center mb-5 h-6" aria-live="polite">
        {answered ? (
          <span className="text-sm font-semibold text-[var(--gold)]" data-testid={`slider-value-${id}`}>
            {current.value} % — {current.label}
          </span>
        ) : (
          <span className="text-xs text-[var(--ink-soft)] italic">{hint}</span>
        )}
      </div>

      <div className="px-2 sm:px-3">
        <div
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={answered ? value : undefined}
          aria-label={statement}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onKeyDown={onKeyDown}
          data-testid={`slider-${id}`}
          className="relative h-10 cursor-pointer select-none touch-none outline-none"
        >
          {/* rail */}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-[var(--beige)]" />
          {/* filled */}
          {answered && (
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-[var(--gold)] transition-all duration-150"
              style={{ width: `${value}%` }}
            />
          )}
          {/* stops */}
          {scale.map((s) => {
            const active = answered && value >= s.value;
            const isCurrent = answered && value === s.value;
            return (
              <button
                key={s.value}
                type="button"
                data-testid={`option-${id}-${s.value}`}
                aria-label={`${s.value} % ${s.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(s.value);
                }}
                className="absolute top-1/2 z-10 flex items-center justify-center"
                style={{ left: `${s.value}%`, transform: "translate(-50%, -50%)" }}
              >
                <span
                  className={`block rounded-full border-2 transition-all duration-150 ${
                    isCurrent
                      ? "h-6 w-6 bg-[var(--gold)] border-[var(--gold)] shadow-md"
                      : active
                      ? "h-4 w-4 bg-[var(--gold)] border-[var(--gold)]"
                      : "h-4 w-4 bg-white border-[var(--gold-light)]"
                  }`}
                />
              </button>
            );
          })}
        </div>

        {/* labels sous chaque cran */}
        <div className="relative mt-1 h-4">
          {scale.map((s) => (
            <span
              key={s.value}
              className="absolute text-[0.7rem] text-[var(--ink-soft)]"
              style={{ left: `${s.value}%`, transform: "translateX(-50%)" }}
            >
              {s.value}%
            </span>
          ))}
        </div>

        {/* ancres */}
        <div className="flex items-center justify-between text-[0.7rem] text-[var(--ink-soft)] mt-3">
          <span>{anchorLeft}</span>
          <span className="text-right">{anchorRight}</span>
        </div>
      </div>
    </div>
  );
};
